import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ImagePlus, LifeBuoy, X, MessageCircle, Mail, RefreshCw, Send } from "lucide-react";
import SupportAgentPicker, { getAgentProfile } from "./SupportAgentPicker";
import { appEventName, siteConfig, storageKey } from "../lib/siteConfig";
import ImagePreviewModal from "./ImagePreviewModal";
import { VerifiedName } from "./VerifiedBadge";

const supportEmail = siteConfig.supportEmail;
const guestKey = storageKey("guest_chat_id");
const OPEN_SUPPORT_EVENT = appEventName("open-support");
const maxImageBytes = 750 * 1024;
const initialMessageCount = 5;
const messageLoadStep = 8;
const SUPPORT_POLL_MS = 12000;

function getGuestId() {
  let id = localStorage.getItem(guestKey);
  if (!id) {
    id = crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(guestKey, id);
  }
  return id;
}

export default function SupportButton() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const isAuthed = user && user !== false;
  const [guestId] = useState(getGuestId);
  const [open, setOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const [chatTicket, setChatTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [visibleMessageCount, setVisibleMessageCount] = useState(initialMessageCount);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  const messageRef = useRef(null);
  const selectedProfile = getAgentProfile(selectedAgent);
  const aiReplyPending = ["pending", "processing"].includes(chatTicket?.ai_reply_status ?? chatTicket?.ai_first_reply_status);

  const loadChat = useCallback(async () => {
    if (authLoading) return;
    try {
      if (isAuthed) {
        const { data } = await api.get("/support/my");
        setChatTicket(data.find((t) => t.subject === "Live agent chat") || null);
        return;
      }
      const { data } = await api.get(`/support/chat/${guestId}`);
      setChatTicket(data?.id ? data : null);
    } catch {
      setChatTicket((current) => current);
    }
  }, [authLoading, guestId, isAuthed]);

  useEffect(() => {
    if (open && !authLoading) loadChat();
  }, [authLoading, open, loadChat]);

  useEffect(() => {
    if (!open || authLoading) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") loadChat();
    }, aiReplyPending ? 2000 : SUPPORT_POLL_MS);
    return () => clearInterval(timer);
  }, [authLoading, open, loadChat, aiReplyPending]);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(OPEN_SUPPORT_EVENT, h);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, h);
  }, []);

  const selectImage = async (file) => {
    if (!file) return;
    try {
      setErr("");
      setAttachment(await readImage(file));
    } catch (e) {
      setErr(e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleImagePaste = (event) => {
    const file = imageFileFromClipboard(event.clipboardData);
    if (!file) return;
    event.preventDefault();
    selectImage(file);
  };

  const submitChat = async (e) => {
    e.preventDefault();
    const message = chatText.trim();
    const picked = attachment;
    if (!message && !picked) return;
    if (!selectedProfile) {
      setErr("Choose Fea, Mellisa, or Jennifer first.");
      return;
    }
    setErr("");
    setLoading(true);
    const temp = {
      id: `local-${Date.now()}`,
      sender: "user",
      message,
      agent_target: selectedProfile?.name,
      image_url: picked?.dataUrl,
      image_name: picked?.name,
      created_at: new Date().toISOString(),
    };
    setChatTicket((current) => appendLocalMessage(current, temp, {
      name: isAuthed ? user.username : "Guest",
      email: isAuthed ? user.email : undefined,
      guestId,
    }));
    setChatText("");
    setAttachment(null);
    try {
      const { data } = await api.post("/support/chat", {
        guest_id: isAuthed ? undefined : guestId,
        name: isAuthed ? user.username : "Guest",
        email: isAuthed ? user.email : undefined,
        message,
        agent_target: selectedProfile?.name,
        image_url: picked?.dataUrl,
        image_name: picked?.name,
      });
      setChatTicket(data);
      setTimeout(loadChat, 150);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const messages = scopedAgentMessages(chatTicket, selectedProfile);
  const hiddenMessageCount = Math.max(0, messages.length - visibleMessageCount);
  const visibleMessages = messages.slice(Math.max(0, messages.length - visibleMessageCount));

  const pickAgent = (agent) => {
    setSelectedAgent(agent);
    setErr("");
    window.setTimeout(() => messageRef.current?.focus(), 0);
  };

  useEffect(() => {
    setVisibleMessageCount(initialMessageCount);
  }, [selectedProfile?.name]);

  useEffect(() => {
    if (!open) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, chatTicket?.last_message_at, messages.length]);

  if (location.pathname.startsWith("/admin") || location.pathname === "/support" || (!isAuthed && location.pathname === "/")) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="support-fab"
        title="Message support"
        aria-label="Message support"
        className={`fixed left-4 md:left-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#2F6FA5] text-white font-semibold text-sm shadow-2xl shadow-black/50 hover:bg-[#4E91C8] hover:-translate-y-0.5 transition-all ${
          isAuthed ? "safe-fixed-chat-button" : "bottom-[calc(1rem+env(safe-area-inset-bottom))]"
        }`}
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Message us</span>
      </button>

      {open && (
        <div
          data-testid="support-dialog"
          className="fixed inset-0 z-[80] bg-[#07111F] text-white md:inset-auto md:right-6 md:bottom-6 md:w-[410px] md:h-[640px] md:rounded-2xl md:border md:border-[#2F6FA5]/30 md:shadow-2xl md:shadow-black/70 overflow-hidden flex flex-col"
        >
          <div className="px-3 py-3 md:px-4 md:py-4 bg-[#081525] border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setOpen(false)}
                data-testid="support-back"
                aria-label="Back"
                className="md:hidden p-2 -ml-1 hover:bg-white/10 rounded-full shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="relative w-11 h-11 rounded-full bg-[#2F6FA5]/15 border border-[#2F6FA5]/40 grid place-items-center shrink-0">
                <LifeBuoy className="w-5 h-5 text-[#2F6FA5]" />
                <span className="absolute right-0 bottom-0 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#081525]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-base font-bold leading-tight truncate">{siteConfig.name} Support</h3>
                <p className="text-xs text-emerald-300">Online queue</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={loadChat} title="Refresh" className="p-2 hover:bg-white/10 rounded-full">
                <RefreshCw className="w-4 h-4" />
              </button>
              <a href={`mailto:${supportEmail}`} title={supportEmail} className="p-2 hover:bg-white/10 rounded-full">
                <Mail className="w-4 h-4" />
              </a>
              <button onClick={() => setOpen(false)} data-testid="support-close" className="p-2 hover:bg-white/10 rounded-full">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="border-b border-white/10 bg-[#07111F] p-3">
            <SupportAgentPicker selectedAgent={selectedAgent} onPick={pickAgent} />
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[radial-gradient(circle_at_top,_rgba(47,111,165,0.09),_transparent_34%),#07111F]">
            {hiddenMessageCount > 0 && (
              <div className="flex justify-center pb-1">
                <button
                  type="button"
                  onClick={() => setVisibleMessageCount((count) => Math.min(messages.length, count + messageLoadStep))}
                  className="rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs font-semibold text-white/60 hover:border-[#2F6FA5]/50 hover:text-white"
                >
                  Load earlier messages ({hiddenMessageCount})
                </button>
              </div>
            )}
            {visibleMessages.map((msg) => (
              <ChatBubble
                key={msg.id || `${msg.sender}-${msg.created_at}`}
                msg={msg}
                onImagePreview={setImagePreview}
                user={isAuthed ? user : null}
              />
            ))}
            {err && (
              <div data-testid="support-error" className="text-sm text-red-300 bg-red-900/20 border border-red-800/40 rounded-lg p-3">
                {err}
              </div>
            )}
          </div>

          <form onSubmit={submitChat} className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3 bg-[#081525] border-t border-white/10">
            {attachment && (
              <div className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-2">
                <img src={attachment.dataUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <span className="min-w-0 flex-1 truncate text-xs text-white/70">{attachment.name}</span>
                <button type="button" onClick={() => setAttachment(null)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Remove image">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => selectImage(e.target.files?.[0])} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title="Add image"
                aria-label="Add image"
                className="w-12 h-12 rounded-full bg-white/[0.06] border border-white/10 text-white grid place-items-center hover:bg-white/[0.1] shrink-0"
              >
                <ImagePlus className="w-4 h-4" />
              </button>
              <textarea
                ref={messageRef}
                data-testid="support-agent-message"
                rows={1}
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onPaste={handleImagePaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submitChat(e);
                  }
                }}
                placeholder={selectedProfile ? `Message ${selectedProfile.name}... or Ctrl+V screenshot` : "Choose an agent first..."}
                className="min-h-[46px] max-h-28 flex-1 resize-none rounded-2xl bg-black/40 border border-white/10 px-4 py-3 text-sm focus:border-[#2F6FA5] focus:outline-none"
              />
              <button
                type="submit"
                disabled={!selectedProfile || loading || (!chatText.trim() && !attachment)}
                data-testid="support-agent-submit"
                className="w-12 h-12 rounded-full bg-[#2F6FA5] text-white grid place-items-center hover:bg-[#4E91C8] disabled:opacity-50 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-2 text-[11px] text-white/35 px-2">Replies appear in this chat window.</p>
          </form>
        </div>
      )}
      <ImagePreviewModal image={imagePreview} onClose={() => setImagePreview(null)} />
    </>
  );
}

function normalizeMessages(ticket) {
  if (!ticket) return [];
  if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
  const out = [];
  if (ticket.message) {
    out.push({ id: `${ticket.id}-user`, sender: "user", message: ticket.message, created_at: ticket.created_at });
  }
  if (ticket.admin_reply) {
    out.push({ id: `${ticket.id}-agent`, sender: "agent", message: ticket.admin_reply, created_at: ticket.updated_at || ticket.created_at });
  }
  return out;
}

function scopedAgentMessages(ticket, selectedProfile) {
  const ticketMessages = normalizeMessages(ticket);
  if (!selectedProfile) {
    return [
      {
        id: "welcome-choose-agent",
        sender: "agent",
        message: "Choose Fea, Mellisa, or Jennifer to start a personal chat.",
        created_at: "",
      },
      ...ticketMessages.filter((msg) => !getAgentProfile(msg.agent_target)),
    ];
  }
  const agentName = selectedProfile.name;
  return [
    {
      id: `welcome-${agentName}`,
      sender: "agent",
      agent_target: agentName,
      message: `You're now messaging ${agentName}.`,
      created_at: "",
    },
    ...ticketMessages.filter((msg) => getAgentProfile(msg.agent_target)?.name === agentName),
  ];
}

function ChatBubble({ msg, onImagePreview, user }) {
  const isAgent = msg.sender === "agent";
  const target = getAgentProfile(msg.agent_target)?.name;
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm border shadow-sm ${
        isAgent
          ? "bg-white/[0.07] border-white/10 text-white rounded-bl-md"
          : "bg-[#2F6FA5] border-[#2F6FA5] text-white rounded-br-md"
      }`}>
        <div className={`text-[10px] mb-1 ${isAgent ? "text-white/45" : "text-black/55"}`}>
          {isAgent ? (msg.is_ai ? (msg.ai_generated ? "AI Support" : "Automated Support") : target || "Agent") : (
            <VerifiedName
              className="[&>svg]:h-3.5 [&>svg]:w-3.5"
              name={user?.username || "You"}
              user={user}
            />
          )}
        </div>
        {msg.image_url && (
          <button
            type="button"
            onClick={() => onImagePreview?.({ src: msg.image_url, name: msg.image_name || "Attachment" })}
            className="mb-2 block max-w-full cursor-zoom-in rounded-xl bg-black/20 text-left outline-none ring-[#2F6FA5]/60 focus:ring-2"
            aria-label="Open image preview"
          >
            <img src={msg.image_url} alt={msg.image_name || "Attachment"} className="block max-h-44 max-w-full rounded-xl object-contain" />
          </button>
        )}
        {msg.message && <div className="whitespace-pre-wrap break-words leading-relaxed">{msg.message}</div>}
      </div>
    </div>
  );
}

function appendLocalMessage(ticket, msg, fallback) {
  const messages = [...normalizeMessages(ticket), msg];
  return {
    ...(ticket || {}),
    id: ticket?.id || `local-${fallback.guestId || Date.now()}`,
    name: ticket?.name || fallback.name || "Guest",
    email: ticket?.email || fallback.email || `${fallback.guestId || "guest"}@${siteConfig.guestEmailDomain}`,
    subject: "Live agent chat",
    status: "open",
    message: msg.message || "[Image]",
    messages,
    last_message_at: msg.created_at,
  };
}

function imageFileFromClipboard(clipboardData) {
  if (!clipboardData) return null;
  const files = Array.from(clipboardData.files || []);
  const file = files.find((item) => item?.type?.startsWith("image/"));
  if (file) return file;

  const items = Array.from(clipboardData.items || []);
  const imageItem = items.find((item) => item.kind === "file" && item.type?.startsWith("image/"));
  return imageItem?.getAsFile?.() || null;
}

function readImage(file) {
  if (!file.type?.startsWith("image/")) throw new Error("Please choose an image file.");
  if (file.size > maxImageBytes) throw new Error("Image is too large. Please use an image under 750 KB.");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ dataUrl: reader.result, name: file.name });
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}
